import { useEffect } from 'react';

/**
 * 저장·등록 결과 알림(#390 — 「버튼에만 표기돼 잘 인지가 안 된다」).
 *
 * 왜: 결과 문구가 화면 맨 위 띠(인라인)에 찍히면 ① 아래로 스크롤했거나 ② 팝업(노선 상세 등)이 떠 있으면
 * 그 띠가 뒤에 가려 **버튼 글자만 바뀐 것처럼** 보인다. 알림은 팝업보다 위, 화면 위 가운데에 띄운다.
 * 표시는 `components/Toaster.tsx`(main.tsx 에서 한 번만 붙인다).
 */
export type ToastKind = 'ok' | 'warn' | 'err';
export type ToastItem = { id: number; text: string; kind: ToastKind };

let seq = 0;
let items: ToastItem[] = [];
const subs = new Set<(list: ToastItem[]) => void>();

function emit() {
  for (const s of subs) s(items);
}

/** 문구만으로 종류를 가른다 — 부르는 곳마다 종류를 넘기지 않아도 경고가 초록으로 뜨지 않게. */
function guessKind(text: string): ToastKind {
  if (/^⚠|실패|못 |없습니다|건너뜀|막혔|거부/.test(text)) return 'warn';
  return 'ok';
}

export function toast(text: string, kind?: ToastKind): number {
  const t = String(text ?? '').trim();
  if (!t) return 0;
  const id = ++seq;
  // 같은 문구가 연달아 오면(두 번 누름 등) 하나만 남긴다.
  items = [...items.filter((x) => x.text !== t), { id, text: t, kind: kind ?? guessKind(t) }].slice(-3);
  emit();
  return id;
}

export function dismissToast(id: number) {
  items = items.filter((x) => x.id !== id);
  emit();
}

export function subscribeToasts(fn: (list: ToastItem[]) => void): () => void {
  subs.add(fn);
  fn(items);
  return () => {
    subs.delete(fn);
  };
}

/** 글자 수에 맞춰 보이는 시간(4~15초). 마우스를 올리면 멈춘다(Toaster). */
export function toastDuration(text: string): number {
  return Math.min(15000, Math.max(4000, 2500 + text.length * 70));
}

/**
 * 화면의 `notice` 상태를 알림으로 띄우고 비운다 — 예전 인라인 띠 대신.
 * 화면 코드는 `setNotice(...)` 그대로 쓴다.
 */
export function useNoticeToast(notice: string | null, setNotice: (v: string | null) => void) {
  useEffect(() => {
    if (!notice) return;
    toast(notice);
    setNotice(null);
  }, [notice, setNotice]);
}
